import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, Check, Copy, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { athleteApi } from '../lib/api';

/**
 * PASSO 4 — INCORPORAR UM CADASTRO DUPLICADO NO OUTRO
 *
 * Três passos, sempre nesta ordem, porque a decisão é irreversível na prática
 * (mesmo com o merge sendo reversível):
 *
 *   1. LISTA   — os grupos de cadastros que parecem a mesma pessoa;
 *   2. COMPARA — os dois lado a lado, campo por campo, escolhendo QUEM fica e
 *                qual valor usar quando os dois discordam (nothing automático);
 *   3. SIMULA  — o que exatamente vai ser movido, e os conflitos. Só depois do
 *                "confirmar" o backend toca em qualquer coisa.
 *
 * O cadastro que perde NÃO é apagado: fica com `status="merged"` apontando para
 * o vencedor, sai das listas e pode ser desfeito.
 */

const ROTULO_MOTIVO = {
  cpf: 'Mesmo CPF',
  nome_nascimento: 'Mesmo nome e mesma data de nascimento',
};

const formatarData = (iso) => {
  if (!iso) return '—';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && a ? `${d}/${m}/${a}` : String(iso);
};

const COLECOES_ROTULO = {
  registrations: 'Inscrições em campeonato',
  medals: 'Medalhas',
  medal_deliveries: 'Entregas de medalha',
  live_rankings: 'Ranking do evento',
  weight_change_log: 'Mudanças de peso',
  attendances: 'Presenças em aula',
  dues: 'Financeiro (mensalidades)',
  ranking_ledger: 'Ranking federativo',
  matches: 'Lutas',
  superfights: 'Superlutas',
  class_groups: 'Turmas',
  documents: 'Documentos',
};

export function DialogoMergeAtleta({ aberto, aoFechar, aoConcluir }) {
  const [passo, setPasso] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [grupo, setGrupo] = useState(null);
  const [vivoId, setVivoId] = useState(null);
  const [escolhas, setEscolhas] = useState({});
  const [simulacao, setSimulacao] = useState(null);
  const [executando, setExecutando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await athleteApi.listarDuplicatas();
      setGrupos(data?.grupos || []);
    } catch {
      toast.error('Erro ao procurar cadastros duplicados');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (aberto) {
      setPasso(1);
      setGrupo(null);
      setSimulacao(null);
      setEscolhas({});
      carregar();
    }
  }, [aberto, carregar]);

  const abrirGrupo = (g) => {
    setGrupo(g);
    // Sugere quem fica: quem tem MAIS histórico (decisão 2). Empate: o primeiro.
    const ordenados = [...g.atletas].sort((a, b) => (b.total_referencias || 0) - (a.total_referencias || 0));
    setVivoId(ordenados[0]._id);
    setEscolhas({});
    setSimulacao(null);
    setPasso(2);
  };

  const perdedorId = grupo?.atletas?.find((a) => a._id !== vivoId)?._id;

  const simular = async () => {
    if (!vivoId || !perdedorId) return;
    setCarregando(true);
    try {
      const { data } = await athleteApi.simularMerge(vivoId, perdedorId);
      setSimulacao(data);
      setPasso(3);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao simular o merge');
    } finally {
      setCarregando(false);
    }
  };

  const confirmar = async () => {
    setExecutando(true);
    try {
      const campos = {};
      // Campos não escolhidos ficam com o vencedor; só o divergente escolhido muda.
      (simulacao?.campos_divergentes || []).forEach((c) => {
        campos[c.campo] = escolhas[c.campo] || 'vivo';
      });
      const { data } = await athleteApi.executarMerge({
        vivo: vivoId,
        perdedor: perdedorId,
        campos,
        confirmar_conflito: (simulacao?.conflitos || []).length > 0 ? true : undefined,
      });
      const movidos = Object.values(data?.movidos || {}).reduce((s, n) => s + (n > 0 ? n : 0), 0);
      toast.success(`Cadastros unificados — ${movidos} registro(s) movido(s)`);
      aoConcluir?.();
      aoFechar?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao unificar os cadastros');
    } finally {
      setExecutando(false);
    }
  };

  const vivo = grupo?.atletas?.find((a) => a._id === vivoId);
  const perdedor = grupo?.atletas?.find((a) => a._id === perdedorId);

  return (
    <Dialog open={aberto} onOpenChange={(v) => { if (!v && !executando) aoFechar?.(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="dialogo-merge">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {passo === 3 ? <Copy className="h-5 w-5 text-[#D4AF37]" aria-hidden="true" />
              : <Users className="h-5 w-5 text-[#D4AF37]" aria-hidden="true" />}
            {passo === 1 && 'Cadastros duplicados'}
            {passo === 2 && 'Comparar os dois cadastros'}
            {passo === 3 && 'Confirmar a unificação'}
          </DialogTitle>
          <DialogDescription>
            {passo === 1 && 'Cadastros que parecem ser a mesma pessoa. O cadastro que perde não é apagado — ele fica marcado como incorporado e pode ser desfeito.'}
            {passo === 2 && 'Escolha quem fica e, onde os dois discordarem, qual valor usar.'}
            {passo === 3 && 'Nada foi alterado ainda. Confira o que vai ser movido.'}
          </DialogDescription>
        </DialogHeader>

        {/* ---------------- PASSO 1: LISTA ---------------- */}
        {passo === 1 && (
          <div className="mt-3">
            {carregando ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
              </div>
            ) : grupos.length === 0 ? (
              <p className="py-10 text-center text-sm text-[#A1A1AA]" data-testid="sem-duplicatas">
                Nenhum cadastro duplicado encontrado. 🎉
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {grupos.map((g) => (
                  <button
                    key={g.chave}
                    type="button"
                    onClick={() => abrirGrupo(g)}
                    data-testid={`grupo-${g.chave}`}
                    className="flex items-center justify-between gap-3 rounded-lg border border-[#27272A] bg-[#141414] p-3 text-left transition-colors hover:border-[#D4AF37]/40"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-white">{g.chave}</p>
                      <p className="text-xs text-[#A1A1AA]">
                        {g.atletas.length} cadastros · {ROTULO_MOTIVO[g.motivo] || 'Dados parecidos'}
                      </p>
                    </div>
                    <ArrowRight size={16} className="shrink-0 text-[#71717A]" aria-hidden="true" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ---------------- PASSO 2: COMPARAÇÃO ---------------- */}
        {passo === 2 && grupo && (
          <div className="mt-3">
            <div className="grid grid-cols-2 gap-3">
              {[vivo, perdedor].filter(Boolean).map((a) => {
                const ehVivo = a._id === vivoId;
                return (
                  <button
                    key={a._id}
                    type="button"
                    onClick={() => setVivoId(a._id)}
                    data-testid={`escolher-vivo-${a._id}`}
                    className={`rounded-lg border p-3 text-left transition-colors ${
                      ehVivo ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#27272A] bg-[#141414] hover:border-[#71717A]'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-micro uppercase tracking-wider text-[#71717A]">
                        {ehVivo ? 'FICA' : 'será incorporado'}
                      </span>
                      {ehVivo && <Check size={14} className="text-[#D4AF37]" aria-hidden="true" />}
                    </div>
                    <p className="mt-1 truncate font-medium text-white">{a.name}</p>
                    <p className="text-xs text-[#A1A1AA]">{formatarData(a.birth_date)}</p>
                    <p className="mt-1 text-xs text-[#A1A1AA]">
                      {a.total_referencias || 0} registro(s) · {a.status || 'sem situação'}
                    </p>
                    <p className="text-micro text-[#71717A]">criado em {a.created_at || '—'}</p>
                  </button>
                );
              })}
            </div>

            <p className="mt-4 text-xs text-[#71717A]">
              Quem tem mais registros costuma ser quem carrega o histórico. Trocar é só clicar no outro cartão.
            </p>

            <Button
              onClick={simular}
              disabled={carregando}
              data-testid="simular-merge"
              className="mt-3 w-full bg-[#D4AF37] text-black hover:bg-[#E5C158]"
            >
              {carregando ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Simular a unificação
            </Button>
          </div>
        )}

        {/* ---------------- PASSO 3: SIMULAÇÃO ---------------- */}
        {passo === 3 && simulacao && (
          <div className="mt-3 flex flex-col gap-4">
            {/* campos divergentes */}
            {simulacao.campos_divergentes.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium text-white">Campos diferentes — escolha qual usar</p>
                <div className="flex flex-col gap-2">
                  {simulacao.campos_divergentes.map((c) => (
                    <div key={c.campo} className="rounded-lg border border-[#27272A] bg-[#141414] p-3">
                      <p className="mb-2 text-xs uppercase tracking-wider text-[#71717A]">{c.rotulo}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[['vivo', c.vivo, vivo?.name], ['perdedor', c.perdedor, perdedor?.name]].map(([lado, valor, nome]) => {
                          const escolhido = (escolhas[c.campo] || 'vivo') === lado;
                          return (
                            <button
                              key={lado}
                              type="button"
                              onClick={() => setEscolhas((p) => ({ ...p, [c.campo]: lado }))}
                              data-testid={`campo-${c.campo}-${lado}`}
                              className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                                escolhido ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-white' : 'border-[#27272A] text-[#A1A1AA] hover:text-white'
                              }`}
                            >
                              <span className="block text-micro text-[#71717A]">{nome}</span>
                              {String(valor || '—')}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* conflitos */}
            {simulacao.conflitos.length > 0 && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3" data-testid="conflitos-merge">
                <p className="flex items-center gap-2 text-sm font-medium text-red-400">
                  <AlertTriangle size={16} aria-hidden="true" /> Atenção antes de continuar
                </p>
                <ul className="mt-2 list-disc pl-5 text-xs text-red-300">
                  {simulacao.conflitos.map((c, i) => (
                    <li key={i}>{c.detalhe}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* o que será movido */}
            <div>
              <p className="mb-2 text-sm font-medium text-white">O que vai ser movido</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {(simulacao.colecoes || []).map((c) => {
                  const qtdVivo = simulacao.vivo.referencias?.[c.colecao] || 0;
                  const qtdPerd = simulacao.perdedor.referencias?.[c.colecao] || 0;
                  const total = qtdVivo + qtdPerd;
                  return (
                    <div
                      key={c.colecao}
                      className={`rounded-md border px-3 py-2 text-xs ${
                        total > 0 ? 'border-[#27272A] bg-[#141414] text-white' : 'border-[#1A1A1A] text-[#52525B]'
                      }`}
                    >
                      <span className="block text-micro text-[#71717A]">{COLECOES_ROTULO[c.colecao] || c.colecao}</span>
                      {total} registro(s)
                      {qtdPerd > 0 && <span className="block text-micro text-[#D4AF37]">movendo {qtdPerd}</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={confirmar}
                disabled={executando}
                data-testid="confirmar-merge"
                className="flex-1 bg-[#D4AF37] text-black hover:bg-[#E5C158]"
              >
                {executando ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                Unificar os cadastros
              </Button>
              <Button
                variant="outline"
                onClick={() => setPasso(2)}
                disabled={executando}
                className="border-[#27272A] text-[#A1A1AA] hover:text-white"
              >
                Voltar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DialogoMergeAtleta;
